import React, { useEffect, useRef, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, FileText, PenLine, HandCoins, Download, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import { FindoSpinner, PageLoader } from "@/components/FindoSpinner";

const money = (n, c = "EUR") => `${Number(n || 0).toFixed(2)} ${c}`;

export default function QuoteView() {
  const { t } = useLang();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signName, setSignName] = useState("");
  const [signConsent, setSignConsent] = useState(false);
  const [offer, setOffer] = useState({ amount: "", message: "" });
  const sigRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  function canvasPos(e) {
    const canvas = sigRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: (clientX - rect.left) * (canvas.width / rect.width), y: (clientY - rect.top) * (canvas.height / rect.height) };
  }
  function startDraw(e) {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = sigRef.current.getContext("2d");
    const { x, y } = canvasPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function moveDraw(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = sigRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#191A54";
    const { x, y } = canvasPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawnRef.current) { hasDrawnRef.current = true; setHasDrawn(true); }
  }
  function endDraw() { drawingRef.current = false; }
  function clearSig() {
    const canvas = sigRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false; setHasDrawn(false);
  }
  useEffect(() => {
    if (signOpen) { hasDrawnRef.current = false; setHasDrawn(false); }
  }, [signOpen]);

  const load = () => api(`/quotes/${token}`, { auth: false }).then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  async function payInvoice() {
    setBusy(true);
    try {
      const res = await api(`/quotes/${token}/pay-invoice`, { auth: false, method: "POST", body: { origin_url: window.location.origin } });
      if (res?.url) { goToCheckout(res.url); return; }
      toast.error(t("mc2.payUnavailable"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function doSign() {
    if (!signName.trim() || !signConsent) return toast.error(t("pub.nameConsentRequired"));
    if (!hasDrawnRef.current) return toast.error(t("pub.drawSignature"));
    setBusy(true);
    try {
      const image = sigRef.current ? sigRef.current.toDataURL("image/png") : "";
      await api(`/quotes/${token}/sign`, { auth: false, method: "POST", body: { name: signName.trim(), accept: true, signature_image: image } });
      toast.success(t("pub.signedSuccess"));
      setSignOpen(false); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function doDecline() {
    if (!window.confirm(t("qv.confirmDecline"))) return;
    setBusy(true);
    try { await api(`/quotes/${token}/decline`, { auth: false, method: "POST" }); toast.success(t("pub.quoteDeclined")); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function doOffer() {
    if (!offer.amount || Number(offer.amount) <= 0) return toast.error(t("qv.invalidAmount"));
    setBusy(true);
    try {
      await api(`/quotes/${token}/offer`, { auth: false, method: "POST", body: { amount: Number(offer.amount), message: offer.message } });
      toast.success(t("pub.offerSubmitted"));
      setOfferOpen(false); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  if (err) return <Centered><p className="text-slate-500" data-testid="quote-error">{t("pub.quoteNotFound")}</p></Centered>;
  if (!data) return <Centered><PageLoader label={t("c.loading")} className="py-8" size={32} /></Centered>;

  const q = data.quote || {};
  const st = data.quote_status;
  const cur = q.currency || "EUR";
  const canAct = st === "sent";
  const canSignContract = st === "counter_accepted";
  const isSigned = st === "signed" || st === "contract_signed";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="quote-view">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <Logo size={30} withText />
          <div className="flex items-center gap-4">
            <a href={`${API}/quotes/${token}/pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-findo-navy hover:underline" data-testid="quote-pdf-link">
              <Download size={16} /> {t("hc.downloadThePdf")}
            </a>
            {data?.invoice && (
              <a href={`${API}/quotes/${token}/invoice.pdf`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:underline" data-testid="quote-invoice-link">
                <FileText size={16} /> {t("qv.invoice")} {data.invoice.number}
              </a>
            )}
            {data?.invoice && data.invoice.status !== "paid" && (
              <button onClick={payInvoice} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-findo-red text-white text-sm font-semibold px-4 py-1.5 hover:bg-findo-red/90 transition-colors disabled:opacity-60" data-testid="pay-invoice-btn">
                <HandCoins size={15} /> {t("qv.payInvoiceOnline")}
              </button>
            )}
            {data?.invoice && data.invoice.status === "paid" && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid="invoice-paid-badge">
                <CheckCircle2 size={16} /> {t("qv.invoicePaid")}
              </span>
            )}
          </div>
        </div>

        <StatusBanner st={st} />

        <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
          <div className="bg-findo-navy text-white p-6 flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-white/60">{st === "contract_signed" ? t("pub.contractLabel") : t("qv.quote")}</p>
              <p className="text-2xl font-display font-bold mt-1">{q.number}</p>
            </div>
            <FileText size={28} className="text-white/40" />
          </div>

          <div className="p-6 grid sm:grid-cols-2 gap-6 text-sm">
            <div>
              <p className="font-semibold text-findo-navy mb-1">{t("qv.issuer")}</p>
              <p className="text-slate-600">{q.company?.name}</p>
              <p className="text-slate-500">{q.company?.address}</p>
              <p className="text-slate-500">{q.company?.email}</p>
              <p className="text-slate-500">{q.company?.phone}</p>
            </div>
            <div>
              <p className="font-semibold text-findo-navy mb-1">{t("qv.client")}</p>
              <p className="text-slate-600">{q.client?.org}</p>
              <p className="text-slate-500">{q.client?.name}</p>
              <p className="text-slate-500">{q.client?.email}</p>
              <p className="text-slate-500">{q.client?.phone}</p>
            </div>
          </div>

          <div className="px-6 pb-2 overflow-x-auto">
            <table className="w-full text-sm" data-testid="quote-items">
              <thead>
                <tr className="text-left text-slate-400 border-b border-border">
                  <th className="py-2">{t("qv.description")}</th><th className="py-2 text-right">{t("pub.qty")}</th>
                  <th className="py-2 text-right">{t("qv.unitHt")}</th><th className="py-2 text-right">{t("qv.vat")}</th>
                  <th className="py-2 text-right">{t("qv.inclTax")}</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="py-2 pr-2">{it.description}</td>
                    <td className="py-2 text-right">{Number(it.qty ?? it.quantity ?? 0)}</td>
                    <td className="py-2 text-right">{Number(it.unit_ht ?? it.unit_price ?? 0).toFixed(2)}</td>
                    <td className="py-2 text-right">{Number(it.tva_pct ?? 0)}%</td>
                    <td className="py-2 text-right font-medium">{Number(it.line_ttc ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 flex flex-col items-end gap-1 text-sm">
            <span className="text-slate-500">{t("qv.totalHt")} : <b className="text-slate-700">{money(q.totals?.total_ht, cur)}</b></span>
            <span className="text-slate-500">{t("qv.vat")} : <b className="text-slate-700">{money(q.totals?.total_tva, cur)}</b></span>
            <span className="text-base text-findo-navy font-bold" data-testid="quote-total-ttc">{t("qv.totalTtc")} : {money(q.totals?.total_ttc, cur)}</span>
            {q.agreed_amount != null && <span className="text-findo-red font-semibold" data-testid="quote-agreed">{t("qv.negotiatedAmount")} : {money(q.agreed_amount, cur)}</span>}
          </div>

          <div className="px-6 pb-6 text-xs text-slate-500 border-t border-border pt-4">
            <p><b>{t("qv.conditions")} :</b> {q.payment_terms}</p>
            <p className="mt-1"><Clock size={12} className="inline mr-1" />{t("qv.quoteValidDays").replace("{n}", q.validity_days)}</p>
            {q.notes && <p className="mt-1">{q.notes}</p>}
            {isSigned && data.signature && (
              <p className="mt-3 text-emerald-600 font-semibold" data-testid="quote-signature">
                {t("qv.signedByOn").replace("{name}", data.signature.name).replace("{date}", String(data.signature.signed_at).slice(0, 10))}
              </p>
            )}
            {isSigned && data.signature?.image && (
              <img src={data.signature.image} alt={t("qv.signatureAlt")} className="mt-1 h-16 object-contain" data-testid="quote-signature-image" />
            )}
            {data.findo_signature && (
              <p className="mt-3 text-findo-navy font-semibold flex items-center gap-1.5" data-testid="quote-findo-signature">
                <FileText size={14} /> {t("qv.countersignedFindo").replace("{name}", data.findo_signature.name).replace("{date}", String(data.findo_signature.signed_at).slice(0, 10))}
              </p>
            )}
          </div>
        </div>

        {(canAct || canSignContract) && (
          <div className="mt-5 flex flex-wrap gap-3 justify-center" data-testid="quote-actions">
            {canAct && (
              <>
                <Button onClick={() => setSignOpen(true)} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="quote-accept-btn">
                  <PenLine size={16} className="mr-1.5" /> {t("qv.acceptAndSign")}
                </Button>
                <Button onClick={() => setOfferOpen(true)} variant="outline" className="rounded-full font-semibold border-findo-navy text-findo-navy" data-testid="quote-offer-btn">
                  <HandCoins size={16} className="mr-1.5" /> {t("pub.makeOffer")}
                </Button>
                <Button onClick={doDecline} disabled={busy} variant="ghost" className="rounded-full font-semibold text-slate-500 hover:text-findo-red" data-testid="quote-decline-btn">
                  <XCircle size={16} className="mr-1.5" /> {t("qv.decline")}
                </Button>
              </>
            )}
            {canSignContract && (
              <Button onClick={() => setSignOpen(true)} className="rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="quote-sign-contract-btn">
                <PenLine size={16} className="mr-1.5" /> {t("qv.signContract")}
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={signOpen} onOpenChange={(o) => !o && setSignOpen(false)}>
        <DialogContent data-testid="quote-sign-dialog">
          <DialogHeader>
            <DialogTitle>{t("pub.eSignature")}</DialogTitle>
            <DialogDescription>{t("pub.eSignatureDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("pub.signerFullName")}</Label><Input value={signName} onChange={(e) => setSignName(e.target.value)} className="mt-1.5 rounded-xl" data-testid="sign-name" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label>{t("qv.handwrittenSignature")}</Label>
                <button type="button" onClick={clearSig} className="text-xs font-semibold text-slate-400 hover:text-findo-red" data-testid="sign-clear">{t("qv.clear")}</button>
              </div>
              <div className="mt-1.5 rounded-xl border border-dashed border-findo-navy/40 bg-slate-50 overflow-hidden">
                <canvas
                  ref={sigRef}
                  width={520}
                  height={160}
                  className="w-full touch-none cursor-crosshair block"
                  style={{ height: 160 }}
                  onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                  data-testid="sign-canvas"
                />
              </div>
              <p className="text-[11px] text-slate-400 mt-1">{t("pub.drawSignatureHint")}</p>
            </div>
            <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={signConsent} onChange={(e) => setSignConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-findo-navy" data-testid="sign-consent" />
              <span>{t("qv.certifyAuthorized").replace("{what}", canSignContract ? t("pub.contractLabel").toLowerCase() : t("qv.quote").toLowerCase())}</span>
            </label>
            <Button onClick={doSign} disabled={busy || !signName.trim() || !signConsent || !hasDrawn} className="w-full rounded-full font-semibold bg-findo-navy" data-testid="sign-submit">
              {busy ? <FindoSpinner size={16} /> : t("qv.signNow")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={offerOpen} onOpenChange={(o) => !o && setOfferOpen(false)}>
        <DialogContent data-testid="quote-offer-dialog">
          <DialogHeader>
            <DialogTitle>{t("pub.makeOffer")}</DialogTitle>
            <DialogDescription>{t("pub.makeOfferDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("qv.yourProposedAmount").replace("{cur}", cur)}</Label><Input type="number" min="1" value={offer.amount} onChange={(e) => setOffer({ ...offer, amount: e.target.value })} className="mt-1.5 rounded-xl" data-testid="offer-amount" /></div>
            <div><Label>{t("qv.messageOptional")}</Label><Textarea value={offer.message} onChange={(e) => setOffer({ ...offer, message: e.target.value })} className="mt-1.5 rounded-xl" data-testid="offer-message" /></div>
            <Button onClick={doOffer} disabled={busy} className="w-full rounded-full font-semibold bg-findo-navy" data-testid="offer-submit">
              {busy ? <FindoSpinner size={16} /> : t("qv.sendMyOffer")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBanner({ st }) {
  const { t } = useLang();
  const map = {
    signed: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: <CheckCircle2 size={18} />, t: t("qv.stSigned") },
    contract_signed: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: <CheckCircle2 size={18} />, t: t("qv.stContractSigned") },
    declined: { c: "bg-slate-100 text-slate-600 border-border", i: <XCircle size={18} />, t: t("qv.stDeclined") },
    countered: { c: "bg-amber-50 text-amber-700 border-amber-200", i: <HandCoins size={18} />, t: t("qv.stCountered") },
    counter_accepted: { c: "bg-findo-red/5 text-findo-red border-findo-red/20", i: <PenLine size={18} />, t: t("qv.stCounterAccepted") },
    counter_refused: { c: "bg-slate-100 text-slate-600 border-border", i: <XCircle size={18} />, t: t("qv.stCounterRefused") },
  };
  const m = map[st];
  if (!m) return null;
  return (
    <div className={`mb-4 rounded-2xl border p-4 flex items-center gap-2.5 font-semibold ${m.c}`} data-testid="quote-status-banner">
      {m.i} {m.t}
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center bg-slate-50">{children}</div>;
}
